/**
 * Everything the public page says, in one place.
 *
 * The manager app reads the restaurant's name, address and phone from
 * `app_settings` — but those rows are behind row-level security, and this page
 * is served to people who are not signed in and never will be. It cannot read
 * them. So the public details live here, in the repo, and are edited here.
 *
 * The details below were taken from the restaurant's own Google Maps listing
 * (maps.app.goo.gl/YJzhhxA1ZQ8eqkYo6). Anything still marked TODO could not be
 * read from it and needs confirming by somebody who works there.
 */
export const site = {
  name: 'The Luck Family Restaurant',
  tagline: 'A family restaurant on Dabholi Road, Katargam — open late, every day.',

  address: '4 Madhav Kunj Society, Dabholi Road, opp. Brilliant School, Katargam, Surat, Gujarat 395004',
  phone: '+91 76001 46360',
  /** TODO: a real inbox, or delete the row — the page hides it when empty. */
  email: '',

  /**
   * The Google listing publishes only the closing time in the signed-out view,
   * so the opening time is the one thing here that is not from it.
   * TODO: confirm the opening time and whether it is the same every day.
   */
  hours: [{ days: 'Every day', time: 'until 2:00 am' }],

  /** The public rating on the Google listing, shown as it is written there. */
  rating: { score: '4.6', count: 46 },

  /**
   * The sections of the menu, from the categories the till actually uses. The
   * dishes named are ones customers pick out in the reviews, so the page and
   * the kitchen agree with each other.
   */
  menu: [
    { name: 'Pavbhaji', note: 'Green and regular, both worth the trip — served with hot pav.' },
    { name: 'Dosa & Paper', note: 'The most-mentioned thing on the menu. Crisp to the edge.' },
    { name: 'Luck Special', note: 'Dry Manchurian, fried rice, and the rest of what the kitchen is known for.' },
    { name: 'Pulao', note: 'Slow-cooked rice, whole spices, no shortcuts.' },
    { name: 'Cold Drinks', note: 'Sosyo, lassi, fresh lime — cold, always.' },
  ],

  /**
   * Real reviews from the Google listing, quoted as written, under the names
   * their authors published them with.
   *
   * One five-star review on the listing is from the owner's own account and is
   * deliberately not here: quoting yourself back as a customer is the sort of
   * thing that gets a listing pulled.
   */
  testimonials: [
    {
      quote: "Amazing food and friendly staff—I'll definitely come back",
      author: 'Himanshi Variya',
      where: 'Google review',
    },
    {
      quote: 'The service is excellent 👌🏻 and the food is delicious.',
      author: 'Jiya Patel',
      where: 'Google review',
    },
    {
      // Google surfaces this one as a highlight without naming its author, so
      // it is credited to the listing rather than to an invented person.
      quote: 'Best green n regular pav bhaji, must try dry Manchurian and fried rice',
      author: '',
      where: 'From the Google listing',
    },
  ],
};

/** Google Maps embeds a plain search query without needing an API key. */
export const mapEmbedSrc = `https://www.google.com/maps?q=${encodeURIComponent(
  `${site.name}, ${site.address}`,
)}&output=embed`;

/** The same query, but opened in the visitor's own maps app. */
export const mapLinkHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  `${site.name}, ${site.address}`,
)}`;

/** Digits only, so `tel:` works from a phone. */
export const telHref = `tel:${site.phone.replace(/[^\d+]/g, '')}`;
